import React, { useState, useRef } from 'react'
import { Edit, useForm } from '@refinedev/antd'
import { Form, Input, InputNumber, Select, Divider, AutoComplete } from 'antd'
import type { Tables } from '@muaa/shared'
import { searchCities, cityLabel } from '../../lib/citySearch'
import type { CityRecord } from '../../lib/citySearch'
import { CLASS_YEAR_OPTIONS } from '../../lib/classYears'

type Profile = Tables<'profiles'>

type CityOption = { value: string; label: string; data: CityRecord }

export function ProfileEdit() {
  const { formProps, saveButtonProps } = useForm<Profile>()
  const [cityOptions, setCityOptions] = useState<CityOption[]>([])
  // The canonical label of the most recent city pick (onSelect). If the operator
  // then free-types a city that no longer matches this label, the state/country
  // derived from that pick are stale and get cleared (see onSearch) — so we never
  // submit e.g. {city:"Toronto", state:"FL"}.
  const lastSelectedLabelRef = useRef<string | null>(null)

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Form.Item label="Name" name="name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Email" name="email" rules={[{ type: 'email' }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Class Year" name="class_year">
          <Select
            showSearch
            allowClear
            placeholder="Select class year"
            options={CLASS_YEAR_OPTIONS}
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item label="Initials" name="initials">
          <Input maxLength={4} />
        </Form.Item>
        <Form.Item label="Avatar URL" name="avatar_url" tooltip="Member photo shown on their app profile + the alumni directory">
          <Input placeholder="https://…/photo.jpg" />
        </Form.Item>
        <Form.Item label="Title" name="title">
          <Input />
        </Form.Item>
        <Form.Item label="Organization" name="org">
          <Input />
        </Form.Item>
        <Form.Item label="Bio" name="bio">
          <Input.TextArea rows={4} />
        </Form.Item>
        <Form.Item label="Tags" name="tags">
          <Select mode="tags" placeholder="Add tags" />
        </Form.Item>
        <Form.Item label="Role" name="role">
          <Select>
            <Select.Option value="member">Member</Select.Option>
            <Select.Option value="admin">Admin</Select.Option>
          </Select>
        </Form.Item>

        {/* ── Location ───────────────────────────────────────── */}
        <Divider orientation="left">Location</Divider>
        <Form.Item
          label="City"
          name="city"
          extra="Start typing to search official city names (free text also accepted)"
        >
          <AutoComplete
            options={cityOptions}
            onSearch={(text) => {
              // Free typing routes through here (onSelect handles picks). If the
              // typed text diverges from the last picked label, the derived
              // state/country are stale — clear state and reset country to US so
              // a free-typed city is never pinned to a stale pick's state. The
              // operator can still re-edit the visible State/Country fields.
              if (lastSelectedLabelRef.current !== null && text !== lastSelectedLabelRef.current) {
                lastSelectedLabelRef.current = null
                formProps.form?.setFieldsValue({ state: undefined, country: 'US' })
              }
              const results = searchCities(text)
              setCityOptions(
                results.map((c) => {
                  const label = cityLabel(c)
                  return { value: label, label, data: c }
                }),
              )
            }}
            onSelect={(value: string, option: CityOption) => {
              const form = formProps.form
              if (form) {
                lastSelectedLabelRef.current = value
                form.setFieldsValue({
                  city: value,
                  state: option.data.country === 'US' ? option.data.region.toUpperCase() : undefined,
                  country: option.data.country,
                })
              }
            }}
            placeholder="Miami, FL"
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="State / Region"
          name="state"
          extra="2-letter state for US (used by the alumni map)"
        >
          <Input placeholder="FL" />
        </Form.Item>
        <Form.Item
          label="Country"
          name="country"
          extra="Country code (US, HT, CA…)"
        >
          <Input placeholder="US" />
        </Form.Item>
        <Form.Item
          label="Latitude (optional)"
          name="latitude"
          extra="Optional — overrides the city lookup. Leave blank to auto-place by city."
          normalize={(v: number | null | undefined) => (v === null || v === undefined || isNaN(v as number) ? null : v)}
        >
          <InputNumber style={{ width: '100%' }} step={0.0001} placeholder="e.g. 25.7617" />
        </Form.Item>
        <Form.Item
          label="Longitude (optional)"
          name="longitude"
          extra="Optional — overrides the city lookup. Leave blank to auto-place by city."
          normalize={(v: number | null | undefined) => (v === null || v === undefined || isNaN(v as number) ? null : v)}
        >
          <InputNumber style={{ width: '100%' }} step={0.0001} placeholder="e.g. -80.1918" />
        </Form.Item>
      </Form>
    </Edit>
  )
}
